import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

const FAQ_DATA: FAQItem[] = [
  {
    id: 'credit-card',
    question: 'Do I need a credit card to start?',
    answer: <p className="text-slate-600 leading-relaxed">No, you can get started for free with no credit card required.</p>,
  },
  {
    id: 'get-started',
    question: 'How quickly can I get started?',
    answer: <p className="text-slate-600 leading-relaxed">You can start reviewing an email in minutes. Just forward your email to your unique project address, share the link, and collect feedback.</p>,
  },
  {
    id: 'stakeholders-signup',
    question: 'Do my stakeholders need to sign up?',
    answer: <p className="text-slate-600 leading-relaxed">No, anyone can review and comment without creating an account. Just send them the link.</p>,
  },
  {
    id: 'email-platform',
    question: 'Will this work with my email platform?',
    answer: <p className="text-slate-600 leading-relaxed">Yes, GreenLight works with any email platform. Send a test email, and you're ready to start collecting feedback.</p>,
  },
  {
    id: 'free-vs-starter',
    question: "What's the difference between Free and Starter?",
    answer: (
      <div className="text-slate-600 leading-relaxed space-y-3">
        <p>Free is great for trying GreenLight on your first campaigns.</p>
        <p>Starter is designed for ongoing use, with more active projects and support for multiple variations so you can manage real workflows without friction.</p>
      </div>
    ),
  },
  {
    id: 'plan-limits',
    question: 'What happens when I hit my plan limits?',
    answer: <p className="text-slate-600 leading-relaxed">You can upgrade anytime to continue working without interruption. Your existing projects and feedback stay exactly as they are.</p>,
  },
  {
    id: 'cancel-downgrade',
    question: 'Can I cancel or downgrade anytime?',
    answer: <p className="text-slate-600 leading-relaxed">Yes, there are no contracts. You can upgrade, downgrade, or cancel at any time.</p>,
  },
  {
    id: 'variations',
    question: 'What are variations?',
    answer: <p className="text-slate-600 leading-relaxed">Variations let you review multiple versions of the same email (like A/B tests or segmented content) in one place, so feedback stays organized and never gets mixed up.</p>,
  },
  {
    id: 'who-is-it-for',
    question: 'Who is GreenLight for?',
    answer: <p className="text-slate-600 leading-relaxed">GreenLight is built for solo email marketers and small teams who want a faster, clearer way to review campaigns and collect feedback.</p>,
  },
];

export const PricingFAQ = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggleItem = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="relative w-full py-24 px-6 bg-slate-50 overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-4xl mx-auto relative z-10">
        <header className="text-center mb-8 md:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-semibold leading-tight tracking-tighter mb-6 text-slate-900 font-display"
          >
            Frequently asked questions
          </motion.h2>
        </header>

        <div className="mt-12 space-y-4">
          {FAQ_DATA.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
            >
              <div className="mb-4 w-full">
                <button
                  onClick={() => toggleItem(item.id)}
                  className={`w-full text-left flex items-center justify-between p-5 md:p-6 bg-white rounded-2xl transition-all duration-300 border ${
                    openId === item.id
                      ? 'border-accent shadow-lg shadow-accent/10'
                      : 'border-slate-100 shadow-sm hover:border-slate-200'
                  }`}
                  aria-expanded={openId === item.id}
                >
                  <span className="flex-1 font-bold text-lg text-slate-900 pr-8">{item.question}</span>
                  <div className="shrink-0 w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400">
                    <motion.div animate={{ rotate: openId === item.id ? 180 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }}>
                      <ChevronDown size={20} />
                    </motion.div>
                  </div>
                </button>
                <AnimatePresence>
                  {openId === item.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 text-slate-600 bg-white/50 rounded-b-2xl -mt-2 border-x border-b border-slate-100 mx-2">
                        {item.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
